import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from "recharts";
import { motion } from "framer-motion";
import { Calendar, AlertTriangle, Clock, CheckCircle, Euro, Filter } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

const formatDateFR = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return date.toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' });
};

const getMonthLabel = (monthKey) => {
  const [year, month] = monthKey.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1, 1);
  return date.toLocaleDateString('fr-FR', { month: 'short', year: '2-digit', timeZone: 'Europe/Paris' });
};

// Calculate days until expiry
const getDaysUntilExpiry = (dateStr) => {
  if (!dateStr) return null;
  const now = new Date();
  const expiry = new Date(dateStr);
  const diffTime = expiry - now;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// Period filter options
const PERIOD_FILTERS = [
  { value: 3, label: "3 mois", color: "bg-red-500" },
  { value: 6, label: "6 mois", color: "bg-orange-500" },
  { value: 12, label: "12 mois", color: "bg-blue-500" },
  { value: 0, label: "Expirés", color: "bg-gray-500" }
];

export default function ContratsEcheances() {
  const { token } = useAuth();
  const [allContracts, setAllContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState(3); // Default: 3 months

  useEffect(() => {
    const fetchContracts = async () => {
      if (!token) return;
      setLoading(true);
      
      try {
        const headers = { Authorization: `Bearer ${token}` };
        // Fetch all contracts to filter client-side
        const res = await axios.get(`${API}/contracts?limit=1000`, { headers });
        setAllContracts(res.data.contracts || []);
      } catch (error) {
        console.error("Error fetching contracts:", error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContracts();
  }, [token]);

  // Filter contracts based on selected period
  const filteredContracts = useMemo(() => {
    const now = new Date();
    
    return allContracts.filter(contract => {
      if (!contract.date_fin) return false;
      
      const daysUntil = getDaysUntilExpiry(contract.date_fin);
      
      if (selectedPeriod === 0) {
        // Show expired contracts
        return daysUntil < 0;
      } else {
        // Show contracts expiring within the selected period (in months)
        const maxDays = selectedPeriod * 30;
        return daysUntil >= 0 && daysUntil <= maxDays;
      }
    }).sort((a, b) => new Date(a.date_fin) - new Date(b.date_fin));
  }, [allContracts, selectedPeriod]);

  // Calculate stats for each period
  const periodStats = useMemo(() => {
    const now = new Date();
    
    const stats = {
      3: { count: 0, mrr: 0 },
      6: { count: 0, mrr: 0 },
      12: { count: 0, mrr: 0 },
      0: { count: 0, mrr: 0 } // Expired
    };
    
    allContracts.forEach(contract => {
      if (!contract.date_fin) return;
      
      const daysUntil = getDaysUntilExpiry(contract.date_fin);
      const mrr = contract.montant_abonnement || 0;
      
      if (daysUntil < 0) {
        stats[0].count++;
        stats[0].mrr += mrr;
      } else {
        if (daysUntil <= 90) {
          stats[3].count++;
          stats[3].mrr += mrr;
        }
        if (daysUntil <= 180) {
          stats[6].count++;
          stats[6].mrr += mrr;
        }
        if (daysUntil <= 365) {
          stats[12].count++;
          stats[12].mrr += mrr;
        }
      }
    });
    
    return stats;
  }, [allContracts]);

  // Chart data for selected period
  const chartData = useMemo(() => {
    const monthsData = {};
    
    filteredContracts.forEach(contract => {
      if (!contract.date_fin) return;
      const month = contract.date_fin.substring(0, 7);
      if (!monthsData[month]) {
        monthsData[month] = { month, mrr: 0, count: 0 };
      }
      monthsData[month].mrr += contract.montant_abonnement || 0;
      monthsData[month].count++;
    });
    
    return Object.values(monthsData).sort((a, b) => a.month.localeCompare(b.month)).map(m => ({
      ...m,
      label: getMonthLabel(m.month),
      isPast: new Date(m.month + '-01') < new Date()
    }));
  }, [filteredContracts]);

  // Calculate total MRR at risk for selected period
  const totalMrrAtRisk = filteredContracts.reduce((sum, c) => sum + (c.montant_abonnement || 0), 0);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 skeleton rounded"></div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <div key={i} className="h-24 skeleton rounded-lg"></div>)}
        </div>
        <div className="h-80 skeleton rounded-lg"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contrats-echeances">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Échéances des Contrats</h1>
        <p className="text-muted-foreground text-sm">Suivi des fins de contrat et renouvellements</p>
      </div>

      {/* Period Filter Buttons */}
      <div className="flex flex-wrap gap-3">
        {PERIOD_FILTERS.map((filter) => {
          const stats = periodStats[filter.value];
          const isSelected = selectedPeriod === filter.value;
          
          return (
            <Button
              key={filter.value}
              variant={isSelected ? "default" : "outline"}
              className={`relative h-auto py-3 px-4 flex flex-col items-start gap-1 min-w-[140px] ${
                isSelected ? "" : "hover:bg-gray-50"
              }`}
              onClick={() => setSelectedPeriod(filter.value)}
              data-testid={`filter-${filter.value}m`}
            >
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${filter.color}`}></div>
                <span className="font-semibold">{filter.label}</span>
              </div>
              <div className="text-xs opacity-80">
                {stats.count} contrat{stats.count > 1 ? 's' : ''} • {formatCurrency(stats.mrr)}/mois
              </div>
            </Button>
          );
        })}
      </div>

      {/* Stats Summary for Selected Period */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-white border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className={`p-3 rounded-lg ${selectedPeriod === 0 ? 'bg-gray-100' : 'bg-orange-50'}`}>
              {selectedPeriod === 0 ? (
                <AlertTriangle className="w-5 h-5 text-gray-600" />
              ) : (
                <Clock className="w-5 h-5 text-orange-500" />
              )}
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {selectedPeriod === 0 ? 'Contrats expirés' : `Échéances < ${selectedPeriod} mois`}
              </p>
              <p className="text-2xl font-bold text-foreground">{filteredContracts.length}</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <Euro className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">MRR concerné</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(totalMrrAtRisk)}</p>
              <p className="text-xs text-muted-foreground">par mois</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-white border">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="p-3 rounded-lg bg-green-50">
              <Calendar className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">CA annuel à risque</p>
              <p className="text-2xl font-bold text-foreground">{formatCurrency(totalMrrAtRisk * 12)}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      {chartData.length > 0 && (
        <Card className="bg-white border">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold">
              Répartition par mois {selectedPeriod === 0 ? '(expirés)' : `(< ${selectedPeriod} mois)`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}k€`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), 'MRR']}
                    labelFormatter={(label) => `Mois: ${label}`}
                  />
                  <Bar dataKey="mrr" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.isPast ? '#ef4444' : '#3b82f6'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contracts List */}
      <Card className="bg-white border">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <Filter className="w-4 h-4" />
            {selectedPeriod === 0 
              ? 'Contrats expirés' 
              : `Contrats arrivant à échéance dans les ${selectedPeriod} prochains mois`
            }
            <Badge variant="secondary" className="ml-2">{filteredContracts.length}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredContracts.length > 0 ? (
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {filteredContracts.map((contract, i) => {
                const daysUntil = getDaysUntilExpiry(contract.date_fin);
                const isExpired = daysUntil < 0;
                const isUrgent = !isExpired && daysUntil <= 30;
                const isSoon = !isExpired && daysUntil <= 90;
                
                return (
                  <motion.div 
                    key={contract.id || i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      isExpired ? 'bg-gray-50 border-gray-200' :
                      isUrgent ? 'bg-red-50 border-red-200' :
                      isSoon ? 'bg-orange-50 border-orange-200' :
                      'bg-blue-50 border-blue-200'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-foreground">{contract.societe}</p>
                        {isExpired && (
                          <Badge variant="destructive" className="text-xs">Expiré</Badge>
                        )}
                        {isUrgent && !isExpired && (
                          <Badge className="text-xs bg-red-500">Urgent</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">{contract.type}</Badge>
                        <span className="text-xs text-muted-foreground">{contract.produit}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono font-bold text-foreground">
                        {formatCurrency(contract.montant_abonnement || 0)}/mois
                      </p>
                      <p className={`text-xs flex items-center justify-end gap-1 ${
                        isExpired ? 'text-gray-600' :
                        isUrgent ? 'text-red-600' :
                        isSoon ? 'text-orange-600' :
                        'text-blue-600'
                      }`}>
                        <Calendar className="w-3 h-3" />
                        {isExpired 
                          ? `Expiré le ${formatDateFR(contract.date_fin)}` 
                          : `${formatDateFR(contract.date_fin)} (${daysUntil}j)`
                        }
                      </p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="p-12 text-center">
              <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
              <p className="text-muted-foreground">
                {selectedPeriod === 0 
                  ? "Aucun contrat expiré" 
                  : `Aucun contrat n'expire dans les ${selectedPeriod} prochains mois`
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
